// ============================================
// FILE: src/components/fashion/DealCard.jsx
// ============================================
//
// Compact card for the Top Deals rail.
//
// Deliberately a different shape from FashionProductCard: a square, smaller
// image and a discount-led hierarchy. A deals shelf that looks identical to
// the grid below it reads as more of the same, and the saving — the only
// reason the shelf exists — gets buried under the product name.

import React from 'react';
import { resolveProductImageUrl, BLANK_PRODUCT_IMAGE, handleProductImageError } from '../../utils/imageHelpers';
import {
  getFinalPrice, getRegularPrice, getSalePrice,
  hasDiscount, getDiscountPercent,
} from '../../utils/priceHelpers';

const DealCard = ({ product, onOpen = () => {}, isDesktop = false }) => {
  const cover = resolveProductImageUrl(
    product.thumbnail_url || product.featured_image_url || product.image_url || product.featured_image
  ) || BLANK_PRODUCT_IMAGE;

  const discounted = hasDiscount(product);
  const percent = getDiscountPercent(product);
  const price = discounted ? getSalePrice(product) : getFinalPrice(product);
  const mrp = getRegularPrice(product);
  const saving = discounted ? Math.round(mrp - price) : 0;

  return (
    <button
      type="button"
      onClick={() => onOpen(product)}
      className="w-full text-left bg-white overflow-hidden transition-shadow hover:shadow-md"
      style={{ border: '1px solid var(--fx-line)', borderRadius: 4 }}
    >
      {/* Square, not 3:4 — a small square reads as a thumbnail rail rather
          than a second product grid, and keeps the card short. */}
      <div className="relative w-full bg-[#f5f5f6]" style={{ aspectRatio: '1 / 1' }}>
        <img
          src={cover}
          alt={product.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          onError={handleProductImageError}
        />

        {percent > 0 && (
          <span
            className="absolute top-0 left-0 px-2 py-1 text-white"
            style={{
              background: 'var(--fx-pink)',
              fontSize: 11,
              fontWeight: 800,
              lineHeight: 1,
              borderBottomRightRadius: 4,
            }}
          >
            {percent}%<span style={{ fontWeight: 600 }}> OFF</span>
          </span>
        )}
      </div>

      <div className="px-2.5 py-2">
        {product.brand && (
          <p className="truncate" style={{ fontSize: 11, fontWeight: 700, color: 'var(--fx-ink)' }}>
            {product.brand}
          </p>
        )}
        <p className="truncate" style={{ fontSize: 12, color: 'var(--fx-ink-2)', lineHeight: 1.3 }}>
          {product.name}
        </p>

        <div className="flex items-baseline gap-1.5 mt-1.5 flex-wrap">
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--fx-ink)' }}>₹{price}</span>
          {discounted && (
            <span style={{ fontSize: 11, color: 'var(--fx-ink-3)', textDecoration: 'line-through' }}>
              ₹{mrp}
            </span>
          )}
        </div>

        {/* The saving in rupees is more persuasive than the percentage alone,
            and it is what the shelf is actually promising. */}
        {saving > 0 && (
          <p
            className="mt-1 inline-block px-1.5 py-0.5"
            style={{ background: '#e8f7f3', color: 'var(--fx-green)', borderRadius: 2, fontSize: 10, fontWeight: 700 }}
          >
            SAVE ₹{saving}
          </p>
        )}
      </div>
    </button>
  );
};

export default DealCard;
